/**
 * Fabric.js Textbox 하이브리드 줄바꿈 패치
 *
 * splitByGrapheme: true 상태에서 공백(단어) 우선 줄바꿈을 구현.
 * 커서 위치/텍스트 선택은 splitByGrapheme: true 모드의 네이티브 로직을 그대로 사용하므로 정상 동작.
 *
 * 동작:
 *   1. 글자를 하나씩 채워 넣다가 줄 너비 초과 시
 *   2. 마지막 공백 위치가 있으면 → 그 공백까지 잘라서 줄바꿈 (공백 우선)
 *   3. 공백이 없으면 → 현재 위치에서 줄바꿈 (글자 단위)
 *
 * 조건: splitByGrapheme: true && wordWrap: true일 때만 활성화
 *
 * 예시 (너비 3글자):
 *   '가나 다라마바사' → '가나 ' / '다라마' / '바사'
 *   '가나다라마바사'  → '가나다' / '라마바' / '사'
 *   '1. 기능 요구사항 입니다' → '1. 기능 ' / '요구사항 ' / '입니다'
 */
import * as fabric from 'fabric';

const originalWrapLine = fabric.Textbox.prototype._wrapLine;

fabric.Textbox.prototype._wrapLine = function (lineIndex, desiredWidth, _ref) {
  let reservedSpace = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;

  // 글머리 기호 들여쓰기: bullet/number/indent가 있는 paragraph는 줄바꿈 가용 너비 축소
  const b = this._bullets?.[lineIndex];
  if (b && (b.type === 'bullet' || b.type === 'number' || b.type === 'indent')) {
    desiredWidth -= Math.round(this.fontSize * 1.2);
  }

  // splitByGrapheme: true + wordWrap: true일 때만 공백 우선 줄바꿈 적용
  if (!this.splitByGrapheme || !this.wordWrap) {
    return originalWrapLine.call(this, lineIndex, desiredWidth, _ref, reservedSpace);
  }

  const { wordsData, largestWordWidth } = _ref;
  const data = wordsData[lineIndex];
  const additionalSpace = this._getWidthOfCharSpacing();
  const actualWidth = desiredWidth - reservedSpace;
  const maxWidth = Math.max(actualWidth, this.dynamicMinWidth);

  const graphemeLines = [];
  let line = [];
  let lineWidth = 0;
  let lineJustStarted = true;

  // 마지막 공백 위치 추적 (공백 우선 줄바꿈용)
  let lastSpaceDataIdx = -1;
  let lastSpaceLineLen = -1;

  for (let i = 0; i < data.length; i++) {
    const { word, width: gWidth } = data[i];
    if (word.length === 0) continue; // 빈 단어 건너뛰기 (빈 논리 줄)
    const grapheme = word[0]; // splitByGrapheme: true → word = [단일 글자]

    // 줄 넘침 체크
    if (lineWidth + gWidth > maxWidth && !lineJustStarted) {
      if (lastSpaceDataIdx >= 0 && lastSpaceLineLen >= 0) {
        // ── 공백 우선 줄바꿈: 마지막 공백까지 잘라서 push ──
        graphemeLines.push(line.slice(0, lastSpaceLineLen + 1)); // 공백 포함

        // 공백 다음부터 재처리
        line = [];
        lineWidth = 0;
        lineJustStarted = true;
        const rewindTo = lastSpaceDataIdx;
        lastSpaceDataIdx = -1;
        lastSpaceLineLen = -1;
        i = rewindTo; // for문이 i++ 하므로 rewindTo+1부터 재시작
        continue;
      } else {
        // ── 공백 없음: 현재 위치에서 글자 단위 줄바꿈 ──
        graphemeLines.push(line);
        line = [];
        lineWidth = 0;
        lineJustStarted = true;
      }
    }

    // 공백 위치 기록 (줄 시작이 아닐 때)
    if (/\s/.test(grapheme) && !lineJustStarted) {
      lastSpaceDataIdx = i;
      lastSpaceLineLen = line.length; // 공백이 추가되기 전의 line.length = 공백의 인덱스
    }

    line.push(grapheme);
    lineWidth += gWidth;
    lineJustStarted = false;
  }

  if (data.length > 0) graphemeLines.push(line);

  if (largestWordWidth + reservedSpace > this.dynamicMinWidth) {
    this.dynamicMinWidth = largestWordWidth - additionalSpace + reservedSpace;
  }

  return graphemeLines;
};
